import React, { useEffect } from 'react';
import './RequesterDetailsModal.css';
import Avatar from './Avatar';

// Below this many ratings the average is noise — the same floor as ProfileModal.js and the phone.
const RATING_MIN_COUNT = 3;

// Who somebody is, before you decide whether to give them your space.
//
// 2026-10-18: rebuilt. It had drifted out of the app's language on every axis at once, and the
// floating ✉ emoji that prompted the rebuild was the least of it:
//
//   - It was headed "User Details" — a label from a database tool. The card is about a PERSON and
//     the person has a name, which is now the heading.
//   - Its numbers were `Helped: 6` key-value rows in bold brand purple, while the same three
//     numbers are a chip strip on your own profile and in the parking history. Three surfaces
//     showing one driver's reputation three different ways.
//   - The ✉ was an emoji in a <span> with an onClick: no label, no hit target, no hover, no
//     keyboard, and nothing about it saying it could be pressed. It is a real button now, with the
//     person's name in it — "Message dinos" says what will happen where a symbol only hints.
const RequesterDetailsModal = ({ isOpen, onClose, requester, onOpenChat }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !requester) return null;

  const rated = (requester.rating_count ?? 0) >= RATING_MIN_COUNT && requester.rating != null;

  return (
    <div className="person-overlay" onClick={onClose} role="presentation">
      <div
        className="person-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="person-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="person-close" onClick={onClose} aria-label="Close">&times;</button>

        <div className="person-identity">
          <Avatar username={requester.username} avatarUrl={requester.avatar_url} size={64} />
          <div className="person-identity-text">
            <h2 className="person-name" id="person-title">{requester.username}</h2>
            <span className="person-since">
              {requester.created_at
                ? `Member since ${new Date(requester.created_at).toLocaleDateString()}`
                : 'Member'}
              {requester.car_type ? ` · ${requester.car_type}` : ''}
            </span>
          </div>
        </div>

        {/* The same three numbers, in the same shape, as your own profile — see ProfileModal.js for
            why these three and not spots_declared, spots_taken or a rank. A stranger's reputation
            should be legible in the same glance as your own. */}
        <div className="profile-stats person-stats">
          <div className="profile-stat">
            <span className="profile-stat-number">{requester.drivers_helped ?? 0}</span>
            <span className="profile-stat-label">Helped</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-number">{requester.spots_shared ?? 0}</span>
            <span className="profile-stat-label">Shared</span>
          </div>
          <div className="profile-stat">
            {/* A dash, not a zero, under the floor: an average of one rating is not an average, and
                a 0 would read as a bad driver rather than a new one. */}
            <span className="profile-stat-number">{rated ? Number(requester.rating).toFixed(1) : '—'}</span>
            <span className="profile-stat-label">Rating</span>
          </div>
        </div>

        <button
          type="button"
          className="btn btn--primary btn--block"
          onClick={() => { onOpenChat(requester); onClose(); }}
        >
          <i className="fas fa-envelope" aria-hidden="true" />
          Message {requester.username}
        </button>
      </div>
    </div>
  );
};

export default RequesterDetailsModal;
